import { equals } from '../equals';
import { freezeCollection, hashCollection } from '../hash';
import { ImmutableSet, type ImmutableSetInit, initializeNativeSet, SET_TYPE_SYMBOL } from './set';

type ImmutableOrderedSetInit<T> = ImmutableSetInit<T>;

let emptyOrderedSet: ImmutableOrderedSet<any> | undefined;

/**
 * Immutable OrderedSet is an ordered collection of unique values.
 */
export class ImmutableOrderedSet<Value> extends ImmutableSet<Value> {
    public static isKeyed = false;
    public static isOrdered = true;

    /**
     * Create an ordered set from an array of values.
     */
    public static create<T>(values: ImmutableOrderedSetInit<T> = []): ImmutableOrderedSet<T> {
        const [set, hashMap] = initializeNativeSet(values);
        if (set.size === 0) {
            emptyOrderedSet =
                emptyOrderedSet || new ImmutableOrderedSet<T>(set, hashMap, SET_TYPE_SYMBOL);
            return emptyOrderedSet;
        }

        // @ts-ignore
        return freezeCollection(new ImmutableOrderedSet<T>(set, hashMap, SET_TYPE_SYMBOL));
    }

    public static isOrderedSet(maybeSet: unknown): maybeSet is ImmutableOrderedSet<unknown> {
        return (
            ImmutableSet.isSet(maybeSet) &&
            'isOrdered' in maybeSet.constructor &&
            typeof maybeSet.constructor.isOrdered === 'boolean' &&
            maybeSet.constructor.isOrdered
        );
    }

    /**
     * Returns a new OrderedSet representing a portion of this OrderedSet from start up to but not including end.
     */
    public slice(begin: number, end: number): this {
        if (begin === 0 && end >= this.size) {
            return this;
        }

        return this.update(this.toArray().slice(begin, end));
    }

    /**
     * Compare the value of this Set to the value of another Set in an ordered fashion
     */
    public equals(other: any): boolean {
        if (this === other) {
            return true;
        }

        if (this.constructor !== other.constructor || this.size !== other.size) {
            return false;
        }

        if (hashCollection(this) !== hashCollection(other)) {
            return false;
        }

        const entries = this.toValuesArray();
        const otherEntries = other.toValuesArray();

        return entries.every((value, index) => {
            const otherValue = otherEntries[index];
            return value === otherValue || equals(value, otherValue);
        });
    }
}
